// Sub-components for the Knowledge Quality Intelligence drawer.
// Split out of KnowledgeQualityDrawer.tsx to keep the drawer under the 700-line
// guideline; mirrors the Concept layer's parts/helpers separation.
import { ShieldCheck, Sparkles } from "lucide-react";
import { ScoreRing, StatTile } from "../Domain";
import type { ProgressBarTone } from "../constants";
import { formatPercent } from "../utils";
import { progressBarTone } from "./knowledgeQualityIntelligenceHelpers";
import { KQ_PRIMARY_LABELS } from "./utils/constants";
import { pluralize } from "./utils/helpers";

export function ProgressBar({
  value,
  tone,
}: {
  value: number | null;
  tone: ProgressBarTone;
}) {
  const v = value ?? 0;
  const pctW = Math.max(0, Math.min(1, v)) * 100;
  const cls = progressBarTone(tone);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="relative h-1.5 flex-1 min-w-[60px] rounded-full bg-white/10 overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${cls.bar}`}
          style={{ width: `${pctW}%` }}
        />
      </div>
      <span
        className={`text-[11px] font-semibold whitespace-nowrap tabular-nums ${cls.text}`}
      >
        {formatPercent(value)}
      </span>
    </div>
  );
}

function buildQualityNarrative({
  entitiesCount,
  richEntities,
  trustPct,
  strongCount,
}: {
  entitiesCount: number;
  richEntities: number;
  trustPct: string;
  strongCount: number;
}): string {
  const entityWord = pluralize(strongCount, "entity", "entities");
  return `${entitiesCount} knowledge entities were extracted, of which ${richEntities} carry rich supporting context. Every entity is backed by directly cited policy evidence with an average trust score of ${trustPct}. ${strongCount} ${entityWord} scored as strongly evidenced.`;
}

export function PrimaryQualityHighlight({
  overallPct,
  strongCount,
  gapsCount,
  richnessPct,
  trustPct,
  entitiesCount,
  richEntities,
  evidenceRecords,
  duplicateEntities,
}: {
  overallPct: string;
  strongCount: number;
  gapsCount: number;
  richnessPct: string;
  trustPct: string;
  entitiesCount: number;
  richEntities: number;
  evidenceRecords: number;
  duplicateEntities: number;
}) {
  const gapWord = pluralize(gapsCount, "gap", "gaps");
  const narrative = buildQualityNarrative({
    entitiesCount,
    richEntities,
    trustPct,
    strongCount,
  });

  return (
    <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_260px]">
      <div className="min-w-0 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="inline-flex items-center rounded-md bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold tracking-wide text-emerald-300 border border-emerald-500/25">
            {KQ_PRIMARY_LABELS.qualityTag}
          </span>
          <span className="text-lg font-bold text-foreground break-words">
            {strongCount} strongly supported · {gapsCount} {gapWord}
          </span>
        </div>

        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-yellow-500 to-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              <Sparkles className="size-3" />
              {KQ_PRIMARY_LABELS.aiTag}
            </span>
            <span className="text-[11px] tracking-wide text-yellow-300 font-semibold">
              {KQ_PRIMARY_LABELS.aiSection}
            </span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-foreground/90 break-words">
            {narrative}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <StatTile
            value={overallPct}
            label={KQ_PRIMARY_LABELS.overallCompleteness}
            tone="emerald"
          />
          <StatTile
            value={richnessPct}
            label={KQ_PRIMARY_LABELS.richness}
            tone="indigo"
          />
          <StatTile
            value={evidenceRecords}
            label={KQ_PRIMARY_LABELS.evidenceRecords}
            tone="cyan"
          />
          <StatTile
            value={duplicateEntities}
            label={KQ_PRIMARY_LABELS.duplicateEntities}
            tone="amber"
          />
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 min-w-0">
        <div className="w-full text-center text-xs text-muted-foreground">
          {KQ_PRIMARY_LABELS.qualityIndex}
        </div>
        <ScoreRing
          value={overallPct}
          icon={<ShieldCheck className="size-5 text-emerald-400" />}
          caption={KQ_PRIMARY_LABELS.ringCaption}
        />
        <div className="w-full flex flex-col gap-1.5 text-[12px]">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">
              {KQ_PRIMARY_LABELS.trust}
            </span>
            <span className="font-semibold text-emerald-400">{trustPct}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">
              {KQ_PRIMARY_LABELS.richness}
            </span>
            <span className="font-semibold text-indigo-400">{richnessPct}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
